// Extraction product & annual extraction calculations

import { ProductEntry, CalculationInputs, MaterialUsage } from "@/types/product/productStep.types";
import { MaterialDocWithId } from "@/types/material";
import { convertToTonnes } from "./unitConversions";
import { getFactorValue, convertToKgForCalculation } from "./conversions";
import { StoneType } from "@/types/stone-type/stone-type.types";

/**
 * Calculate product revenue based on quantity and ratios
 * Formula: fat_prod = (q × fat_ext) / prod_ext
 */
export const calculateProductRevenue = (quantity: number, totalRevenue: number, totalProduction: number): number => {
  if (totalProduction <= 0) return 0;
  return Math.round(((quantity * totalRevenue) / totalProduction) * 100) / 100;
};

/**
 * Calculate ratio by quantity produced
 * Formula: r_prod = q / prod_ext
 */
export const calculateRatioByQuantity = (quantity: number, totalProduction: number): number => {
  if (totalProduction === 0) return 0;
  // Preserve high precision for internal calculations
  return quantity / totalProduction;
};

/**
 * Calculate ratio by revenue
 * Formula: r_fat = fat_prod / fat_ext
 */
export const calculateRatioByRevenue = (productRevenue: number, totalRevenue: number): number => {
  if (totalRevenue === 0) return 0;
  // Preserve high precision for internal calculations
  return productRevenue / totalRevenue;
};

/**
 * Calculate the total footprint for materials used by a specific product
 * @param materialsUsed Array of materials used by the product
 * @param materialDocs Array of material documents from database
 * @param type "carbon" or "hydric"
 * @returns Total footprint
 */
export const calculateProductSpecificMaterialFootprints = (
  materialsUsed: MaterialUsage[],
  materialDocs: MaterialDocWithId[],
  type: "carbon" | "hydric"
): number => {
  let total = 0;

  for (const materialUsage of materialsUsed) {
    const materialDoc = materialDocs.find((doc) => doc.id === materialUsage.materialId);
    if (materialDoc && materialUsage.quantity > 0) {
      // Convert tons to kg if necessary
      const converted = convertToKgForCalculation(materialUsage.quantity, materialUsage.unit);
      const factor = getFactorValue(materialDoc, materialUsage.unit, type);
      const contribution = converted.quantity * factor;
      total += contribution;
    }
  }

  // Don't round here to preserve precision - rounding will be done at display level
  return total;
};

/**
 * Determines the correct allocation ratio (physical or economic) for extraction phase carbon calculations.
 * @param generalData General data from the form.
 * @returns 'physical' or 'economic'.
 */
export const getExtractionAllocationType = (generalData: CalculationInputs["generalData"]): "physical" | "economic" => {
  const pvpCaro = generalData.pvp_caro_ext || 0;
  const pvpBarato = generalData.pvp_barato_ext || 0;

  if (pvpBarato <= 0) {
    // Default to economic if pvp_barato is not set, to avoid division by zero.
    return "economic";
  }

  const rd = pvpCaro / pvpBarato;
  return rd <= 5 ? "physical" : "economic";
};

/**
 * Calculate product carbon footprint from materials (Extraction phase)
 * Formula: pc_ext-materiais = (allocation_ratio * pc_mats) / q
 * Uses rd-based allocation (same pattern as energy calculations)
 */
export const calculateProductCarbonFootprintMaterials = (
  entry: ProductEntry,
  _inputs: CalculationInputs,
  materialDocs: MaterialDocWithId[] = []
): number => {
  const { annualProduction } = entry;

  if (annualProduction <= 0) return 0;

  // Materials are already pre-allocated in the form (quantity = annualTotal * materialSpecificRatio)
  // No need to multiply by ratio again
  const pc_mats = calculateProductSpecificMaterialFootprints(entry.materialsUsed, materialDocs, "carbon");

  const result = pc_mats / annualProduction;

  return result;
};

/**
 * Calculate product hydric footprint from materials based on diagram
 * Formula: ph_ext-materiais = (r_fat * ph_mats) / q
 * NOTE: Hydric footprint ALWAYS uses economic allocation (ratioByRevenue)
 */
export const calculateProductHydricFootprintMaterials = (
  entry: ProductEntry,
  _inputs: CalculationInputs,
  materialDocs: MaterialDocWithId[] = []
): number => {
  const { annualProduction } = entry;

  if (annualProduction <= 0) return 0;

  // Hydric materials use economic allocation - use materialsUsedHydric (always economic-allocated)
  const hydricMaterials = entry.materialsUsedHydric || entry.materialsUsed;
  const ph_mats = calculateProductSpecificMaterialFootprints(hydricMaterials, materialDocs, "hydric");

  // Materials are already pre-allocated with economic ratio in materialsUsedHydric
  // No need to multiply by ratio again
  const result = ph_mats / annualProduction;
  return result;
};

/**
 * Validate that product totals match general data
 */
export const validateProductTotals = (
  entries: ProductEntry[],
  generalData: { prod_ext: number; fat_ext: number }
): { isProductionValid: boolean; isRevenueValid: boolean } => {
  const productionTarget = Number(generalData.prod_ext) || 0;
  const revenueTarget = Number(generalData.fat_ext) || 0;

  // If no entries or general data values are zero, consider it invalid
  if (entries.length === 0 || productionTarget <= 0 || revenueTarget <= 0) {
    return { isProductionValid: false, isRevenueValid: false };
  }

  // Convert annual production to tonnes for each product
  const totalProductionResult = entries.reduce(
    (acc, entry) => {
      const result = convertToTonnes(entry);
      acc.value += result.value;
      if (result.usedDefaultThickness) acc.usedDefaultThickness = true;
      if (result.usedDefaultDensity) acc.usedDefaultDensity = true;
      return acc;
    },
    { value: 0, usedDefaultThickness: false, usedDefaultDensity: false }
  );
  const totalProduction = totalProductionResult.value;

  const totalRevenue = entries.reduce((sum, entry) => {
    const revenue = Number(entry.productRevenue) || 0;
    return sum + revenue;
  }, 0);

  // If calculated totals are zero, consider it invalid
  if (totalProduction <= 0 || totalRevenue <= 0) {
    return { isProductionValid: false, isRevenueValid: false };
  }

  // Round to whole numbers for validation (matches display formatting)
  const isProductionValid = Math.round(totalProduction) === Math.round(productionTarget);
  const isRevenueValid = Math.round(totalRevenue) === Math.round(revenueTarget);

  return { isProductionValid, isRevenueValid };
};

/**
 * Calculate product carbon footprint from electricity, fuels, and residue (Extraction phase)
 * Formula: pc_ext-ecr = (allocation_ratio * (pc_ele + pc_comb + pc_res)) / q
 */
export const calculateProductCarbonFootprintElectricity = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { annualProduction, ratioByQuantity, ratioByRevenue } = entry;
  const { generalData, consumptions } = inputs;

  if (annualProduction <= 0) return 0;

  const allocationType = getExtractionAllocationType(generalData);
  const ratio = allocationType === "physical" ? ratioByQuantity : ratioByRevenue;

  let totalCarbonConsumption = 0;
  if (consumptions.extraction) {
    // Mixed company
    totalCarbonConsumption =
      (consumptions.extraction.electricity?.total_pc_ele || 0) +
      (consumptions.extraction.fuels?.total_pc_comb || 0) +
      (consumptions.extraction.residue?.total_pc_res || 0);
  } else {
    // Single activity company
    totalCarbonConsumption =
      (consumptions.electricity?.total_pc_ele || 0) +
      (consumptions.fuels?.total_pc_comb || 0) +
      (consumptions.residue?.total_pc_res || 0);
  }

  const result = (ratio * totalCarbonConsumption) / annualProduction;

  return result;
};

/**
 * Calculate product carbon footprint from electricity only (Extraction phase)
 */
export const calculateProductCarbonFootprintElectricityOnly = (
  entry: ProductEntry,
  inputs: CalculationInputs
): number => {
  const { annualProduction, ratioByQuantity, ratioByRevenue } = entry;
  const { generalData, consumptions } = inputs;

  if (annualProduction <= 0) return 0;

  const allocationType = getExtractionAllocationType(generalData);
  const ratio = allocationType === "physical" ? ratioByQuantity : ratioByRevenue;

  let totalElectricityConsumption = 0;
  if (consumptions.extraction) {
    // Mixed company
    totalElectricityConsumption = consumptions.extraction.electricity?.total_pc_ele || 0;
  } else {
    // Single activity company
    totalElectricityConsumption = consumptions.electricity?.total_pc_ele || 0;
  }

  const result = (ratio * totalElectricityConsumption) / annualProduction;

  return result;
};

/**
 * Calculate product carbon footprint from fuels only (Extraction phase)
 */
export const calculateProductCarbonFootprintFuels = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { annualProduction, ratioByQuantity, ratioByRevenue } = entry;
  const { generalData, consumptions } = inputs;

  if (annualProduction <= 0) return 0;

  const allocationType = getExtractionAllocationType(generalData);
  const ratio = allocationType === "physical" ? ratioByQuantity : ratioByRevenue;

  let totalFuelsConsumption = 0;
  if (consumptions.extraction) {
    // Mixed company
    totalFuelsConsumption = consumptions.extraction.fuels?.total_pc_comb || 0;
  } else {
    // Single activity company
    totalFuelsConsumption = consumptions.fuels?.total_pc_comb || 0;
  }

  const result = (ratio * totalFuelsConsumption) / annualProduction;

  return result;
};

/**
 * Calculate product carbon footprint from residue only (Extraction phase)
 */
export const calculateProductCarbonFootprintResidue = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { annualProduction, ratioByQuantity, ratioByRevenue } = entry;
  const { generalData, consumptions } = inputs;

  if (annualProduction <= 0) return 0;

  const allocationType = getExtractionAllocationType(generalData);
  const ratio = allocationType === "physical" ? ratioByQuantity : ratioByRevenue;

  let totalResidueConsumption = 0;
  if (consumptions.extraction) {
    // Mixed company
    totalResidueConsumption = consumptions.extraction.residue?.total_pc_res || 0;
  } else {
    // Single activity company
    totalResidueConsumption = consumptions.residue?.total_pc_res || 0;
  }

  const result = (ratio * totalResidueConsumption) / annualProduction;

  return result;
};

/**
 * Calculate total product carbon footprint (Extraction phase)
 * Formula: pc_prod = pc_ext-materiais + pc_ext-ecr
 */
export const calculateProductCarbonFootprintTotal = (carbonMaterials: number, carbonElectricity: number): number => {
  // Don't round here to preserve precision for further calculations
  return carbonMaterials + carbonElectricity;
};

/**
 * Calculate product water footprint from electricity and fuels (Extraction phase)
 * Formula: ph_ext-ec = (r_fat * (ph_ele + ph_comb)) / q
 * NOTE: Hydric footprint ALWAYS uses economic allocation regardless of unit
 */
export const calculateProductHydricFootprintElectricity = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { annualProduction, ratioByRevenue } = entry;
  const { consumptions } = inputs;

  if (annualProduction <= 0) return 0;

  let totalHydricConsumption = 0;
  if (consumptions.extraction) {
    // Mixed company
    totalHydricConsumption =
      (consumptions.extraction.electricity?.total_ph_ele || 0) + (consumptions.extraction.fuels?.total_ph_comb || 0);
  } else {
    // Single activity company
    totalHydricConsumption =
      (consumptions.electricity?.total_ph_ele || 0) + (consumptions.fuels?.total_ph_comb || 0);
  }

  const result = (ratioByRevenue * totalHydricConsumption) / annualProduction;

  return result;
};

/**
 * Calculate product water footprint from water (Extraction phase)
 * Formula: ph_ext-agua = (r_fat * ph_agua) / q
 * NOTE: Hydric footprint ALWAYS uses economic allocation regardless of unit
 */
export const calculateProductHydricFootprintWater = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { annualProduction, ratioByRevenue } = entry;
  const { consumptions } = inputs;

  if (annualProduction <= 0) return 0;

  let totalWaterConsumption = 0;
  if (consumptions.extraction) {
    // Mixed company
    totalWaterConsumption = consumptions.extraction.water?.total_ph_agua || 0;
  } else {
    // Single activity company
    totalWaterConsumption = consumptions.water?.total_ph_agua || 0;
  }

  const result = (ratioByRevenue * totalWaterConsumption) / annualProduction;

  return result;
};

/**
 * Calculate total product water footprint (Extraction phase)
 * Formula: ph_prod = ph_ext-materiais + ph_ext-ec + ph_ext-agua
 */
export const calculateProductHydricFootprintTotal = (
  hydricMaterials: number,
  hydricElectricity: number,
  hydricWater: number
): number => {
  // Don't round here to preserve precision for further calculations
  return hydricMaterials + hydricElectricity + hydricWater;
};

/**
 * Calculate all extraction product footprints according to Assimagra spec.
 * @param entry The product entry to calculate for
 * @param allEntries All product entries (for total production)
 * @param calculationInputs Object with all necessary data for calculations
 * @param materialDocs Material documents for emission/water factors
 * @param stoneDocs Array of stone type documents
 * @returns Object with all calculated values
 */
export function calculateExtractionProductFootprints(
  entry: ProductEntry,
  _allEntries: ProductEntry[],
  calculationInputs: CalculationInputs,
  materialDocs: MaterialDocWithId[],
  stoneDocs: StoneType[]
) {
  // Calculate and add footprint values directly to entry.materialsUsed
  if (entry.materialsUsed && entry.materialsUsed.length > 0) {
    entry.materialsUsed = entry.materialsUsed.map((mat) => {
      const matDoc = materialDocs?.find((m) => m.id === mat.materialId);
      
      if (!matDoc) {
        console.warn(`[extraction.ts] Material document not found for materialId: ${mat.materialId}, materialName: ${mat.materialName}`);
      }
      
      const carbon = matDoc ? getFactorValue(matDoc, mat.unit, "carbon") * mat.quantity : 0;
      const hydric = matDoc ? getFactorValue(matDoc, mat.unit, "hydric") * mat.quantity : 0;
      
      return {
        ...mat,
        carbon_footprint: carbon,
        hydric_footprint: hydric,
      };
    });
  }

  // Ratios are pre-calculated and attached to the entry object

  // Individual footprint calculations
  const pc_ext_materiais = calculateProductCarbonFootprintMaterials(entry, calculationInputs, materialDocs);
  const pc_ext_ecr = calculateProductCarbonFootprintElectricity(entry, calculationInputs);

  // Individual component calculations
  const pc_ele = calculateProductCarbonFootprintElectricityOnly(entry, calculationInputs);
  const pc_comb = calculateProductCarbonFootprintFuels(entry, calculationInputs);
  const pc_res = calculateProductCarbonFootprintResidue(entry, calculationInputs);

  const pc_prod = calculateProductCarbonFootprintTotal(pc_ext_materiais, pc_ext_ecr); // Transport is handled separately

  const ph_ext_materiais = calculateProductHydricFootprintMaterials(entry, calculationInputs, materialDocs);
  const ph_ext_ec = calculateProductHydricFootprintElectricity(entry, calculationInputs);
  const ph_ext_agua = calculateProductHydricFootprintWater(entry, calculationInputs);
  const ph_prod = calculateProductHydricFootprintTotal(ph_ext_materiais, ph_ext_ec, ph_ext_agua); // Transport is handled separately

  return {
    // Preserve full precision here; UI components control displayed decimals
    pc_ext_materiais: pc_ext_materiais,
    pc_ext_ecr: pc_ext_ecr,
    pc_ele: pc_ele,
    pc_comb: pc_comb,
    pc_res: pc_res,
    pc_prod: pc_prod,
    ph_ext_materiais: ph_ext_materiais,
    ph_ext_ec: ph_ext_ec,
    ph_ext_agua: ph_ext_agua,
    ph_prod: ph_prod,
    density: stoneDocs.find((doc) => doc.id === entry.stoneType)?.dens_pedra?.value || entry.density || 1,
  };
}
